import {FC, useContext, useState} from 'react';
import {RecoilState, useRecoilState, useSetRecoilState} from 'recoil';
import {toast} from 'react-toastify';
import {Box, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {SaveSetModal} from './SaveSetModal';
import {ModalContainer} from '../../modals/ModalContainer';
import {ConfirmAction} from '../../modals/ConfirmAction';
import {EventBusContext} from '../../../providers/EventBus';
import {FilterService} from '../../../api/services/filters.service';
import {FilterType, IFiltersSet} from '../../../shared/types/filtersSet.types';
import {blue} from '@mui/material/colors';

interface IFiltersSetItem {
  set: IFiltersSet;
  closeAccordion: () => void;
  filtersState: RecoilState<FilterType[]>;
  filtersSetsState: RecoilState<IFiltersSet[]>;
  currentFiltersSetState: RecoilState<IFiltersSet | null>;
}

export const FiltersSetItem: FC<IFiltersSetItem> = ({
  set,
  closeAccordion,
  filtersSetsState,
  filtersState,
  currentFiltersSetState,
}) => {
  const setFilters = useSetRecoilState(filtersState);
  const [filtersSet, setFiltersSet] = useRecoilState(currentFiltersSetState);
  const setFiltersSets = useSetRecoilState(filtersSetsState);
  const [openModalInnerEffect, setOpenModalInnerEffect] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const {bus} = useContext(EventBusContext);

  const toggleOpenModalInnerEffect = () => {
    setOpenModalInnerEffect((prev) => !prev);
  };

  const toggleConfirmDelete = () => {
    setOpenDelete((prev) => !prev);
  };

  const handleDeleteFiltersSet = async (setModalLoading: (loading: boolean) => void) => {
    setModalLoading(true);

    try {
      await FilterService.delete(set.id);
      setFiltersSets((prev) => prev.filter((i) => i.id !== set.id));

      toast.success(`Набор "${set.name}" удален`, {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      toggleConfirmDelete();
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditFiltersSetIE = (setModalLoading: (loading: boolean) => void) => async (set: IFiltersSet) => {
    try {
      setModalLoading(true);
      await FilterService.update({
        ...set,
        filters: JSON.stringify(set.filters),
      });
      setFiltersSets((prev) => prev.map((i) => (i.id === set.id ? set : i)));

      toast.success('Набор фильтров изменен', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      toggleOpenModalInnerEffect();
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          backgroundColor: filtersSet?.name === set.name ? blue[50] : 'inherit',
          justifyContent: 'space-between',
          '&:hover': {
            backgroundColor: '#DDDDDD',
          },
          p: 0.25,
        }}
      >
        <Tooltip title={set.name}>
          <Box
            onClick={() => {
              setFilters(set.filters);
              setFiltersSet(set);
              closeAccordion();
            }}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '180px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              pl: 1,
            }}
          >
            <Typography noWrap>{set.name}</Typography>
          </Box>
        </Tooltip>
        <Stack direction="row" spacing={1}>
          <IconButton
            sx={{
              '&:hover': {
                backgroundColor: '#e3e3e3',
              },
            }}
            size="small"
            onClick={toggleOpenModalInnerEffect}
          >
            <EditIcon color="primary" />
          </IconButton>
          <IconButton size="small" onClick={toggleConfirmDelete}>
            <DeleteIcon color="error" />
          </IconButton>
        </Stack>
      </Stack>
      {openModalInnerEffect && (
        <ModalContainer
          sideEffect={handleEditFiltersSetIE}
          renderModal={(sideEffect, loading) => (
            <SaveSetModal
              filtersState={filtersState}
              currentFiltersSetState={currentFiltersSetState}
              innerEffect={true}
              open={openModalInnerEffect}
              handleSideEffect={sideEffect}
              loading={loading}
              handleClose={toggleOpenModalInnerEffect}
              set={set}
              filters={set.filters}
            />
          )}
        />
      )}
      {openDelete && (
        <ModalContainer
          sideEffect={handleDeleteFiltersSet}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={openDelete}
              handleClose={toggleConfirmDelete}
              handleSideEffect={sideEffect}
              title="Подтвердите удаление"
              buttonTitle="Удалить"
            />
          )}
        />
      )}
    </>
  );
};
